import React from "react";

import { cn } from "lib/utils";

import { Button } from "js/components/ui/button";

type DashboardLoadingProps = {
  /**
   * When true, renders an inline slow-state hint with retry action.
   */
  showSlowState?: boolean;
  onRetry?: () => void;
  className?: string;
};

const shimmerClass =
  "relative overflow-hidden bg-slate-200/80 motion-safe:animate-pulse motion-reduce:animate-none";

const SkeletonBlock = ({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn("rounded-lg", shimmerClass, className)} aria-hidden>
    {children}
  </div>
);

const DashboardLoading = ({
  showSlowState = false,
  onRetry,
  className,
}: DashboardLoadingProps) => {
  return (
    <div
      className={cn("flex flex-col gap-4", className)}
      role="status"
      aria-live="polite"
      aria-label="Cargando tu panel principal"
      aria-busy="true"
    >
      <span className="sr-only">Cargando tus datos...</span>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between lg:gap-6">
          <SkeletonBlock className="h-9 w-48" />
          <div className="flex w-full flex-wrap justify-end gap-3 lg:w-auto">
            <SkeletonBlock className="h-[112px] w-full min-w-[200px] flex-1 rounded-xl sm:max-w-[240px]" />
            <SkeletonBlock className="h-[112px] w-full min-w-[200px] flex-1 rounded-xl sm:max-w-[240px]" />
            <SkeletonBlock className="h-[112px] w-full min-w-[200px] flex-1 rounded-xl sm:max-w-[240px]" />
          </div>
        </div>
      </div>

      <div className="mt-2 rounded-2xl border border-slate-200/70 bg-white/90">
        <div className="flex flex-col gap-4 p-4 pb-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <SkeletonBlock className="h-10 w-full min-w-[240px] rounded-md md:max-w-md" />
            <div className="flex w-full flex-1 flex-wrap gap-2 md:justify-start">
              <SkeletonBlock className="h-8 w-28 rounded-full" />
              <SkeletonBlock className="h-8 w-24 rounded-full" />
              <SkeletonBlock className="h-8 w-32 rounded-full" />
            </div>
            <div className="flex w-full justify-end md:w-auto md:pl-4">
              <SkeletonBlock className="h-10 w-full rounded-md md:w-[250px]" />
            </div>
          </div>
        </div>
        <div className="border-t border-slate-200/70 bg-white/95 px-2 pb-4 pt-3">
          <div className="space-y-4">
            <div className="overflow-hidden rounded-xl border border-slate-200/70">
              <div className="border-b border-slate-200/70 bg-slate-50/70">
                <div className="grid grid-cols-5 gap-4 px-6 py-4">
                  <SkeletonBlock className="h-4 w-20" />
                  <SkeletonBlock className="h-4 w-16" />
                  <SkeletonBlock className="h-4 w-24" />
                  <SkeletonBlock className="h-4 w-20" />
                  <SkeletonBlock className="h-4 w-16" />
                </div>
              </div>
              <div className="divide-y divide-slate-200/70">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="grid grid-cols-5 gap-4 px-6 py-4">
                    <SkeletonBlock className="h-4 w-full rounded-md" />
                    <SkeletonBlock className="h-4 w-full rounded-md" />
                    <SkeletonBlock className="h-4 w-full rounded-md" />
                    <SkeletonBlock className="h-4 w-full rounded-md" />
                    <SkeletonBlock className="h-4 w-full rounded-md" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSlowState && (
        <div className="flex flex-col items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-700 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="font-medium">
              Esto está tardando más de lo previsto.
            </p>
            <p className="text-amber-600">
              Verifica tu conexión o vuelve a intentarlo en unos segundos.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onRetry}
            disabled={!onRetry}
            className="border-amber-300 text-amber-700 hover:bg-amber-100"
          >
            Reintentar
          </Button>
        </div>
      )}
    </div>
  );
};

export default DashboardLoading;
